import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { KeyValueIsPrimary, KeyValueTypeModel } from '@models/common';
import { FieldConditionModel, ScreenConditionModel } from '@models/module/workflowConditionalTransition';
import { WorkflowState } from '@models/module/workflowState';
import { ScreenConditionComponent } from '@module-definition/complex-conditions/start-screen-condition/start-screen-condition.component';
import { TranslateService } from '@ngx-translate/core';
import { CipoListControl } from '@shared/components/fields/common';
import { SaveButtonActions, SaveButtonType } from '@shared/components/save-button/common';
import { SaveButtonComponent } from '@shared/components/save-button/save-button.component';
import { CipoDialogHeaderComponent } from '@shared/dialogs';
import { CipoDialogModule } from '@shared/modules/cipo-dialog.module';
import { NotificationService } from '@shared/services';
import { CipoSelectComponent } from '../../../../shared/components/fields/cipo-select/cipo-select.component';
import { FieldConditionComponent } from '../field-condition/field-condition.component';

type ComplexConditionGroup = {
  id: number;
  position: number;
  transitionId?: number;
  stateId?: number;
  templateId?: number;
  groupId?: number;
  fieldConditions: FieldConditionModel[];
  screenConditions: ScreenConditionModel[];
  stateLabel?: string;
  printLabel?: string;
};

@Component({
  selector: 'cipo-add-edit-complex-condition',
  templateUrl: './add-edit-complex-condition.component.html',
  styleUrls: ['./add-edit-complex-condition.component.scss'],
  imports: [
    CipoDialogModule,
    CipoSelectComponent,
    ScreenConditionComponent,
    CommonModule,
    SaveButtonComponent,
    FieldConditionComponent,
    CipoDialogHeaderComponent,
  ],
})
export class AddEditComplexConditionComponent {
  dialogData = inject<{
    condition: ComplexConditionGroup;
    isTransitional: boolean;
    states?: WorkflowState[];
    templates?: KeyValueIsPrimary<number>[];
    conditions: ComplexConditionGroup[];
    operators: KeyValueTypeModel<number>[];
    fieldDict: KeyValueTypeModel<number>[];
    allowMultipleConditions?: boolean;
    allowStartScreenConditions?: boolean;
  }>(MAT_DIALOG_DATA);
  private translate = inject(TranslateService);
  readonly dialogRef = inject(MatDialogRef<AddEditComplexConditionComponent>);
  notification = inject(NotificationService);

  control = signal<CipoListControl | null>(null);
  selectedTransitionId = signal<number | null>(null);
  fieldConditions = signal<FieldConditionModel[]>([]);
  screenConditions = signal<ScreenConditionModel[]>([]);
  validFieldConditions = signal<FieldConditionModel[]>([]);
  validScreenConditions = signal<ScreenConditionModel[]>([]);
  anyChanges = signal<boolean>(false);

  isSaveEnabled = computed(() => {
    const hasField = this.fieldConditions().length > 0;
    const hasScreen = this.screenConditions().length > 0;

    const allFieldValid =
      hasField &&
      this.validFieldConditions().length === this.fieldConditions().length &&
      this.validFieldConditions().length > 0;

    const allScreenValid =
      hasScreen &&
      this.validScreenConditions().length === this.screenConditions().length &&
      this.validScreenConditions().length > 0;

    const hasValidConditions =
      (hasField && hasScreen && allFieldValid && allScreenValid) ||
      (hasField && !hasScreen && allFieldValid) ||
      (!hasField && hasScreen && allScreenValid);

    const condition = this.dialogData.condition ?? { id: 0 };

    if (condition.id === 0) {
      return hasValidConditions && this.selectedTransitionId() !== null;
    }

    return (
      (hasValidConditions || this.selectedTransitionId() !== this.initialTransitionId || this.anyChanges()) &&
      this.selectedTransitionId() !== null
    );
  });
  showDiscardChanges = computed(() => this.anyChanges());

  SAVE_BUTTON_TYPE = SaveButtonType;
  private initialFieldConditions: FieldConditionModel[] = [];
  private initialScreenConditions: ScreenConditionModel[] = [];
  private initialTransitionId: number | null = null;

  constructor() {
    const {
      condition = { fieldConditions: [], screenConditions: [], stateId: null, templateId: null },
      isTransitional,
      states = [],
      templates = [],
    } = this.dialogData ?? {};
    const initialValue = isTransitional ? (condition?.stateId ?? null) : (condition?.templateId ?? null);

    const labelKey = isTransitional
      ? 'complexConditions.transitionCondition.transitionToStateRequired'
      : 'complexConditions.conditionalPrintout.toPrintoutRequired';
    const label = this.translate.instant(labelKey);

    const options = states.length
      ? states.map(({ id: key, name: value }) => ({ key, value }))
      : templates.map(({ key, value }) => ({ key, value }));

    const instance = new CipoListControl(initialValue, {
      label,
      options,
    });

    this.control.set(instance);
    this.selectedTransitionId.set(initialValue);
    this.initialTransitionId = initialValue;

    instance.valueChanges.subscribe(val => {
      this.selectedTransitionId.set(val);
      this.anyChanges.set(val !== initialValue);
    });

    if (condition.fieldConditions.length) {
      const initial = condition.fieldConditions.map(fc => ({ ...fc }));
      this.initialFieldConditions = [...initial];
      this.fieldConditions.set(initial);
    } else {
      this.initialFieldConditions = [];
      this.fieldConditions.set([]);
    }

    if (condition.screenConditions.length) {
      const initialScreen = condition.screenConditions.map(sc => ({ ...sc }));
      this.initialScreenConditions = [...initialScreen];
      this.screenConditions.set(initialScreen);
    } else {
      this.initialScreenConditions = [];
      this.screenConditions.set([]);
    }
  }

  onFieldConditionsAllChanged(all: FieldConditionModel[]) {
    this.fieldConditions.set([...all]);
  }

  onFieldConditionsChanged(updated: FieldConditionModel[]) {
    this.validFieldConditions.set(updated);
  }

  discardChanges() {
    this.fieldConditions.set([...this.initialFieldConditions]);
    this.screenConditions.set([...this.initialScreenConditions]);
    this.anyChanges.set(false);
    if (this.control()) {
      this.control()!.setValue(this.initialTransitionId);
      this.selectedTransitionId.set(this.initialTransitionId);
    }
  }

  save(type: SaveButtonActions) {
    const transitionId = this.selectedTransitionId();

    if (transitionId !== null) {
      const condition: ComplexConditionGroup = this.dialogData.condition
        ? { ...this.dialogData.condition }
        : {
            id: 0,
            position: 0,
            fieldConditions: [],
            screenConditions: [],
          };

      condition.fieldConditions = this.validFieldConditions();
      condition.screenConditions = this.validScreenConditions();

      this.notification.success('complexConditions.general.saveCondition');

      if (type === SaveButtonActions.SaveAndClose) {
        this.dialogRef.close(condition);
      } else {
        this.clearChangesOnSave();
      }
    }
  }

  clearChangesOnSave() {
    this.initialFieldConditions = this.validFieldConditions().map(fc => ({ ...fc }));
    this.initialScreenConditions = this.validScreenConditions().map(sc => ({ ...sc }));
    this.fieldConditions.set([...this.initialFieldConditions]);
    this.screenConditions.set([...this.initialScreenConditions]);
    this.anyChanges.set(false);
    if (this.control()) {
      this.control()!.setValue(this.selectedTransitionId());
    }
  }

  onFieldConditionsDirty(dirty: boolean) {
    this.anyChanges.set(dirty);
  }

  onAddFieldCondition() {
    const updated = [
      ...this.fieldConditions(),
      {
        fieldId: null,
        operatorId: null,
        value: null,
        fieldLabel: '',
        fieldTypeId: 0,
      },
    ];
    this.fieldConditions.set(updated);
    this.anyChanges.set(true);
  }

  onAddStartScreenCondition() {
    const updated = [
      ...this.screenConditions(),
      {
        screenId: null,
        operatorId: null,
        value: null,
      },
    ];
    this.screenConditions.set(updated);
    this.anyChanges.set(true);
  }

  onScreenConditionsChanged(updated: ScreenConditionModel[]) {
    this.validScreenConditions.set(updated);
  }

  onScreenConditionsAllChanged(all: ScreenConditionModel[]) {
    this.screenConditions.set([...all]);
  }

  onScreenConditionsDirty(dirty: boolean) {
    this.anyChanges.set(dirty);
  }
}
